import React, { useEffect, useState } from 'react';

import { Accept, FileRejection, useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { v4 as uuid } from 'uuid';

import { ReactComponent as Cancel } from '../../../assets/icons/cancel.svg';
import { ReactComponent as Check } from '../../../assets/icons/check_circle.svg';
import { ReactComponent as TrashNE } from '../../../assets/icons/trash-ne.svg';
import { ReactComponent as Upload } from '../../../assets/icons/upload.svg';
import { UPLOAD_FILE_ERROR } from '../../constants/toast';
import { IDropzoneValidator } from '../../interfaces/fileUploaderContainer';
import { TRANSLATIONS_GLOBAL } from '../../translations/commons';
import { GenericObject, GenericVoidFunction } from '../../types/genericTypes';
import { fileToBase64, bytesToSize, textList, isStringWithValue } from '../../utils/utils';
import StyledCircularProgress from '../StyledCircularProgress/StyledCircularProgress';
import ToastNE from '../ToastNE/ToastNE';

import './DropzoneFileUploader.scss';

interface IDropzoneFileUploaderProps {
  validator: IDropzoneValidator;
  updateBase64: GenericVoidFunction;
  selectedFile?: File;
  inputTitle?: string;
  externalError?: string;
  customData?: GenericObject;
  ActionButton?: JSX.Element;
  saveFileName?: GenericVoidFunction;
  type?: 'file' | 'image';
  loading?: boolean;
  reset?: boolean;
  correctedFileMessage?: boolean;
}

const DropzoneFileUploader = ({
  updateBase64,
  externalError,
  customData,
  validator,
  type = 'file',
  selectedFile,
  inputTitle,
  saveFileName,
  loading = false,
  ActionButton,
  correctedFileMessage = false,
  reset
}: IDropzoneFileUploaderProps) => {
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string>();
  const [file, setFile] = useState<File>();
  const [isToastOpen, setIsToastOpen] = useState(false);
  const { t } = useTranslation();

  const saveFile = async (file: File) => {
    updateBase64(await fileToBase64(file));
    setFile(file);
  };

  const handleDrop = (acceptedFiles: File[], fileRejections: FileRejection[]) => {
    if (acceptedFiles.length > 0) {
      if (file) deleteFile();
      saveFile(acceptedFiles[0]);
    }
    if (fileRejections.length > 0) {
      setIsToastOpen(true);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleDrop,
    accept: validator.fileFormats as Accept,
    multiple: validator.maxFileCount > 1,
    maxFiles: validator.maxFileCount,
    maxSize: validator.maxFileSize,
    noDragEventsBubbling: true,
    onError: (error: Error) => {
      setError(t(error.message));
      setIsToastOpen(true);
    }
  });

  const deleteFile = () => {
    setError(undefined);
    setFile(undefined);
    updateBase64(undefined);
  };

  const filesInfo = (): string => {
    const fileNameText = `${file!.name} • ${bytesToSize(file!.size, 0, 'decimal')}`;
    if (customData?.prefixFileInfo) {
      return `${customData?.prefixFileInfo} / ${fileNameText}`;
    }
    return fileNameText;
  };

  const getFileFormats = (): string => {
    const formatsList = Object.values(validator.fileFormats).flat();

    return `${t(TRANSLATIONS_GLOBAL.fileUploader.imageFormat)}: ${textList(
      formatsList.map((f) => `${f}`),
      ', ',
      ` ${t(TRANSLATIONS_GLOBAL.fileUploader.or)} `
    )}`;
  };

  const getTitle = (): string => {
    if (inputTitle) return t(inputTitle);

    return type === 'file'
      ? t(TRANSLATIONS_GLOBAL.fileUploader.dragFile)
      : t(TRANSLATIONS_GLOBAL.fileUploader.dragImageNE);
  };

  const handleCloseToast = () => {
    setIsToastOpen(false);
  };

  const getFileText = () => {
    return type === 'file'
      ? t(TRANSLATIONS_GLOBAL.fileUploader.findIt)
      : t(TRANSLATIONS_GLOBAL.fileUploader.toSearchIt);
  };

  useEffect(() => {
    if (selectedFile) {
      setFile(selectedFile);
    }
  }, [selectedFile]);

  useEffect(() => {
    setSuccess(!!file && !loading);
    if (!!file && saveFileName) {
      saveFileName(filesInfo());
    }
  }, [file, loading]);

  useEffect(() => {
    if (externalError) {
      setError(externalError);
    }
  }, [externalError]);

  useEffect(() => {
    if (reset !== undefined) {
      deleteFile();
    }
  }, [reset]);

  return (
    <>
      <div
        className={`dropzone-file-uploader ${success ? 'upload-complete' : ''} ${isDragActive ? 'dragging' : ''}`}
        data-experience="new"
      >
        {success ? (
          <div className={`file-uploaded ${error ? 'error' : 'success'}`}>
            {
              <div className="flex flex-column items-center justify-center w-full p-6">
                <div className="flex flex-column items-center justify-center w-full gap-4">
                  <div className="flex flex-column items-center justify-center w-full gap-1">
                    {!isStringWithValue(error) ? <Check /> : <Cancel />}
                    <div className={`font-bold ${!isStringWithValue(error) ? 'ne-font-body-lg' : 'ne-font-body-xs'}`}>
                      {file && filesInfo()}
                    </div>
                    {isStringWithValue(error) && (
                      <div className="flex flex-column items-start justify-center">
                        <div className="font-bold ne-font-body-lg" key={`error${uuid()}`}>
                          {error}
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="flex align-center justify-between gap-4 whitespace-nowrap">
                    {ActionButton && <div className="flex justify-center">{ActionButton}</div>}
                    <div className="flex mt-2 items-center">
                      <TrashNE />
                      {correctedFileMessage ? (
                        <span className="ne-font-body-sm underline cursor-pointer" {...getRootProps()}>
                          {t(TRANSLATIONS_GLOBAL.fileUploader.correctedFile)}
                        </span>
                      ) : (
                        <span className="ne-font-body-sm underline cursor-pointer" onClick={deleteFile}>
                          {t(TRANSLATIONS_GLOBAL.fileUploader.deleteFile)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            }
          </div>
        ) : (
          <div
            {...getRootProps()}
            className="dropzone flex flex-column items-center justify-center p-9 w-full text-center"
          >
            {loading ? (
              <div>
                <div className="w-full px-3">
                  <StyledCircularProgress size={32} />
                </div>
                <div className="flex flex-column items-center gap-2">
                  <p className="ne-font-body-lg font-bold m-0">{t(TRANSLATIONS_GLOBAL.fileUploader.loading.title)}</p>
                  <p className="ne-font-body-xs m-0">{t(TRANSLATIONS_GLOBAL.fileUploader.loading.description)}</p>
                </div>
              </div>
            ) : (
              <>
                <input {...getInputProps()} data-testid="dropzone" />
                <Upload style={{ width: '32px', height: '32px' }} />
                <div className="ne-font-body-xs">
                  <div className="ne-font-body-lg font-bold">{getTitle()}</div>
                  {`${t(TRANSLATIONS_GLOBAL.fileUploader.or)} `}
                  <span>{t(TRANSLATIONS_GLOBAL.fileUploader.clickHere)}</span>
                  {` ${getFileText()}`}
                </div>
                <span className="ne-font-body-xs">
                  {!!validator.fileFormats && getFileFormats()}
                  {!!validator.maxFileSize &&
                    ` • ${t(TRANSLATIONS_GLOBAL.fileUploader.maxSize)}: ${bytesToSize(
                      validator.maxFileSize,
                      0,
                      'decimal'
                    )}`}
                </span>
              </>
            )}
          </div>
        )}
      </div>
      <ToastNE
        title={t(UPLOAD_FILE_ERROR.title)}
        description={t(UPLOAD_FILE_ERROR.description)}
        icon={UPLOAD_FILE_ERROR.icon}
        open={isToastOpen}
        button={UPLOAD_FILE_ERROR.button}
        buttonText={t(UPLOAD_FILE_ERROR.buttonText)}
        buttonAction={handleCloseToast}
        handleClose={handleCloseToast}
      />
    </>
  );
};

export default DropzoneFileUploader;
